 
 
 
import { Component, Input, Output, EventEmitter, input, signal, effect, forwardRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatNativeDateModule, DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { FormControl } from '@angular/forms';
import { DateFormatter } from '../../../../../utilities/date-formatter.util';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MonthYearDateAdapter, LT_MONTH_YEAR_FORMATS } from '../../../../../utilities/lt-month-year-date-adapter';


@Component({
  selector: 'app-lt-month-year-picker',
  imports: [
    CommonModule,
    MatDatepickerModule,
    MatInputModule,
    MatFormFieldModule,
    MatNativeDateModule,
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => LTMonthYearPickerComponent),
      multi: true,
    },
    {
      provide: DateAdapter,
      useClass: MonthYearDateAdapter,
    },
    {
      provide: MAT_DATE_FORMATS,
      useValue: LT_MONTH_YEAR_FORMATS,
    },
  ],
  template: `
    <mat-form-field [appearance]="fillMode" class="lt-month-year-picker">
      @if(label){
        <mat-label>{{label}}</mat-label>
      }
      <input
        matInput
        [formControl]="dateControl"
        [matDatepicker]="picker"
        [min]="minDate()"
        [max]="maxDate()"
        [placeholder]="placeholder"
        readonly />
        @if(hint){
          <mat-hint>{{hint}}</mat-hint>
        }
        @else if(format){
          <mat-hint>{{format}}</mat-hint>
        }
        <mat-datepicker-toggle matIconSuffix [for]="picker"></mat-datepicker-toggle>
        @if (clearButton && dateControl.value) {
          <button matSuffix matIconButton
            aria-label="Clear"
            (click)="clearDate($event)">
            <mat-icon>close</mat-icon>
          </button>
        }
        <mat-datepicker #picker
          startView="multi-year"
          (monthSelected)="setMonthAndYear($event, picker)"></mat-datepicker>
    </mat-form-field>
  `,
  styles: `
    .lt-month-year-picker {
      width: 100%;
    }

    `,
})
export class LTMonthYearPickerComponent implements ControlValueAccessor {
  //#region Inputs

  @Input() fillMode: 'outline' | 'fill' = 'outline';
  @Input() placeholder = 'Enter a month and year';
  @Input() label = 'Month-Year';
  @Input() hint = null;
  @Input() clearButton = true;

  required = input(false);

  //#region Disabled
  @Input() set disabled(isDisabled: boolean) {
    if (isDisabled) {
      this.dateControl.disable();
    } else {
      this.dateControl.enable();
    }
  }
  get disabled(): boolean {
    return this.dateControl.disabled;
  }
  //#endregion

  //#region format
  private _format = signal('MM/yyyy');
  @Input() set format(value: string) {
    if (value) {
      this._format.set(value);
      this.showFormattedHint = true;
    }
  }
  get format(): string {
    return this._format();
  }
  // Whether to show the formatted date hint
  showFormattedHint = false;
  //#endregion

  //#region MinDate
  private _minDate = signal<Date | null>(null);
  @Input() set min(value: Date | null) {
    this._minDate.set(value);
  }
  get minDate() {
    return this._minDate;
  }
  //#endregion

  //#region MaxDate
  private _maxDate = signal<Date | null>(null);
  @Input() set max(value: Date | null) {
    this._maxDate.set(value);
  }
  get maxDate() {
    return this._maxDate;
  }
  //#endregion

  //#region Value
  private _value = signal<Date | null>(null);
  @Input() set value(value: Date | null) {
    this._value.set(value);
    this.dateControl.setValue(value, { emitEvent: false });
  }
  get value(): Date | null {
    return this._value();
  }
  //#endregion

  //#endregion

  // Form control for the datepicker
  dateControl = new FormControl<Date | null>(null);

  // ouput
  @Output() valueChange = new EventEmitter<Date | null>();

  // ControlValueAccessor
  onChange: any = () => {};
  onTouched: any = () => {};

  constructor() {
    // Subscribe to date control changes
    this.dateControl.valueChanges.subscribe(value => {
      this._value.set(value);
      this.onChange(value);
      this.valueChange.emit(value);
    });

    // Effect to update display when format changes
    effect(() => {
      this._format(); // Track format changes
      this.showFormattedHint = true;
    });
  }

  // Format display date based on user format
  formatDisplayDate(date: Date): string {
    if (!date) return '';
    return DateFormatter.formatDate(date, this.format);
  }

  // Clear the date
  clearDate(event: Event): void {
    event.stopPropagation();
    this.dateControl.setValue(null);
    this._value.set(null);
    this.onChange(null);
    this.valueChange.emit(null);
  }

  writeValue(value: Date): void {
    this._value.set(value);
    this.dateControl.setValue(value, {emitEvent: false});
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
    this.dateControl.valueChanges.subscribe(() => {
      this.onTouched();
    });
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.dateControl.disable();
    } else {
      this.dateControl.enable();
    }
  }

  /**
   * When a month is selected, set the date to the first day of that month
   * and close the datepicker
   */
  setMonthAndYear(date: Date, datepicker: any): void {
    const selectedDate = new Date(date.getFullYear(), date.getMonth(), 1);
    this.dateControl.setValue(selectedDate);
    datepicker.close();
  }
}
